import React, { useState, useCallback, useRef } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  MarkerF,
  StandaloneSearchBox,
} from "@react-google-maps/api";
import { LocateFixed, Search } from "lucide-react";

type Location = {
  lat: number;
  lng: number;
  address?: string;
};

type Props = {
  onClose: () => void;
  onSelectLocation: (location: Location) => void;
  showSearchBox?: boolean;
};

const containerStyle = {
  width: "100%",
  height: "100%",
};

function LocationMapPicker({
  onClose,
  onSelectLocation,
  showSearchBox = true,
}: Props) {
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: `${process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY}`,
    libraries: ["places"],
  });

  const [mapRef, setMapRef] = useState<google.maps.Map | null>(null);
  const [searchBoxRef, setSearchBoxRef] =
    useState<google.maps.places.SearchBox | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [tempLocation, setTempLocation] = useState<Location | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [locationName, setLocationName] = useState<string>("");

  const center = {
    lat: 21.1446,
    lng: 79.1603,
  };

  const onLoad = useCallback((map: google.maps.Map) => {
    setMapRef(map);
    map.setZoom(5);
  }, []);

  const handleMapClick = (e: google.maps.MapMouseEvent) => {
    if (!e.latLng) return;

    const lat = e.latLng.lat();
    const lng = e.latLng.lng();

    setTempLocation({ lat, lng });
    setShowConfirm(true); // Show confirmation popup

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode(
      { location: { lat: lat, lng: lng } },
      (results, status) => {
        let address = "";
        if (status === "OK" && results && results[0]) {
          address = results[0].formatted_address;
        }

        onSelectLocation({
          lat: lat,
          lng: lng,
          address,
        });

        setLocationName(address);
      }
    );
  };

  const handleConfirm = () => {
    if (!tempLocation) return;

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode(
      { location: { lat: tempLocation.lat, lng: tempLocation.lng } },
      (results, status) => {
        let address = "";
        if (status === "OK" && results && results[0]) {
          address = results[0].formatted_address;
        }

        onSelectLocation({
          lat: tempLocation.lat,
          lng: tempLocation.lng,
          address,
        });

        onClose(); // Close the modal
      }
    );
  };

  const handleCancel = () => {
    setTempLocation(null);
    setShowConfirm(false);
  };

  return isLoaded ? (
    <div className="relative w-full h-full">
      {showSearchBox && (
        <div className="absolute top-2  z-10 lg:w-[50%] p-3 w-[100%]">
          <StandaloneSearchBox
            onLoad={(ref) => setSearchBoxRef(ref)}
            onPlacesChanged={() => {
              if (!searchBoxRef) return;
              const places = searchBoxRef.getPlaces();
              if (places && places.length > 0) {
                const place = places[0];
                const lat = place.geometry?.location?.lat();
                const lng = place.geometry?.location?.lng();
                if (lat && lng) {
                  const location = { lat, lng };
                  setTempLocation(location);
                  mapRef?.panTo(location);
                  mapRef?.setZoom(14);
                  setShowConfirm(true); // ← Add this to trigger confirm popup
                }
              }
            }}
          >
            <input
              ref={inputRef}
              type="text"
              defaultValue={locationName}
              placeholder="Search for a location"
              className="w-full text-sm bg-white text-black p-2 pl-4 rounded-3xl shadow-md"
            />
            {/* <span> <Search /></span> */}
          </StandaloneSearchBox>
        </div>
      )}

      <GoogleMap
        mapContainerStyle={containerStyle}
        center={tempLocation || center}
        onLoad={
          onLoad // Ensure map zooms in even without interaction
        }
        onClick={handleMapClick}
        options={{
          streetViewControl: false,
          fullscreenControl: false,
          mapTypeControl: false,
          zoomControl: true, // optional – set to false to remove zoom buttons
          restriction: {
            latLngBounds: {
              north: 85,
              south: -85,
              west: -179.99,
              east: 179.99,
            },
            strictBounds: true,
          },
        }}
      >
        {tempLocation && (
          <MarkerF
            position={tempLocation}
            draggable={true}
            onDragEnd={(e) => {
              const lat = e.latLng?.lat();
              const lng = e.latLng?.lng();
              if (lat !== undefined && lng !== undefined) {
                setTempLocation({ lat, lng });
                setShowConfirm(true); // Optionally show confirm again
              }
            }}
          />
        )}
      </GoogleMap>

      {
        <button
          className="absolute right-0 bottom-44 w-max p-2 bg-white text-gray-600 mr-2 mb-2 rounded-full shadow flex justify-center"
          onClick={() => {
            if (navigator.geolocation) {
              navigator.geolocation.getCurrentPosition(
                (position) => {
                  const lat = position.coords.latitude;
                  const lng = position.coords.longitude;

                  const location = { lat, lng };
                  setTempLocation(location);
                  if (mapRef) {
                    mapRef.panTo(location);
                    mapRef.setZoom(15); // Zoom in closer to show exact location
                  }
                  setShowConfirm(true);
                  const geocoder = new window.google.maps.Geocoder();
                  geocoder.geocode(
                    { location: { lat: lat, lng: lng } },
                    (results, status) => {
                      let address = "";
                      if (status === "OK" && results && results[0]) {
                        address = results[0].formatted_address;
                      }

                      onSelectLocation({
                        lat: lat,
                        lng: lng,
                        address,
                      });

                      setLocationName(address);
                    }
                  );
                },
                (error) => {
                  console.error("Geolocation error:", error);
                  alert(
                    "Unable to fetch your current location. Please allow permission."
                  );
                }
              );
            } else {
              alert("Geolocation is not supported by your browser.");
            }
          }}
        >
          <LocateFixed className="w-6 h-6" />
        </button>
      }

      {showConfirm && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-white p-4 rounded shadow-lg flex gap-4 items-center">
          <p className="dark:text-black text-black text-nowrap">
            Confirm this location?
          </p>
          <button
            onClick={handleConfirm}
            className="px-4 py-1 bg-green-500 text-white rounded"
          >
            Yes
          </button>
          <button
            onClick={handleCancel}
            className="px-4 py-1 bg-gray-300 text-black rounded"
          >
            No
          </button>
        </div>
      )}
    </div>
  ) : null;
}

export default LocationMapPicker;
